import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, endpoints } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

const CATEGORIES = ["Complaint", "Feedback", "Suggestion", "Sexual Harassment (POSH)", "Child Protection (POCSO)", "Financial Irregularity", "Other"];

export const GrievanceForm = () => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", category: "Complaint", subject: "", description: "", is_anonymous: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/grievances", form);
      setDone(res);
      toast.success("Grievance received. We’ll acknowledge within 3 working days.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please try again.");
    } finally { setLoading(false); }
  };

  if (done) return (
    <div data-testid="grievance-form-success" className="rounded-xl border border-[hsl(var(--green)/0.25)] bg-[hsl(var(--surface-tint-green))] p-6"><CheckCircle2 className="h-8 w-8 text-[hsl(var(--green))]" /><h3 className="heading-3 mt-2">Grievance received</h3><p className="lead mt-1">Reference <code className="text-xs">{done.id?.slice(0,8)}</code>. Our Grievance Officer will acknowledge within 3 working days and provide an update within 30 days.</p></div>
  );

  return (
    <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2" data-testid="grievance-form">
      <Field label={form.is_anonymous ? "Full name (optional)" : "Full name*"}><Input required={!form.is_anonymous} data-testid="grievance-form-full_name" value={form.full_name} onChange={set("full_name")} /></Field>
      <Field label={form.is_anonymous ? "Email (optional)" : "Email*"}><Input required={!form.is_anonymous} type="email" data-testid="grievance-form-email" value={form.email} onChange={set("email")} /></Field>
      <Field label="Phone"><Input data-testid="grievance-form-phone" value={form.phone} onChange={set("phone")} /></Field>
      <Field label="Category*">
        <Select value={form.category} onValueChange={set("category")}>
          <SelectTrigger data-testid="grievance-form-category"><SelectValue /></SelectTrigger>
          <SelectContent>{CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
        </Select>
      </Field>
      <div className="md:col-span-2"><Field label="Subject*"><Input required data-testid="grievance-form-subject" value={form.subject} onChange={set("subject")} /></Field></div>
      <div className="md:col-span-2"><Field label="Description*"><Textarea required rows={5} data-testid="grievance-form-description" value={form.description} onChange={set("description")} placeholder="Please share facts, dates, and any references. Do not share sensitive identifiers of victims." /></Field></div>
      <div className="md:col-span-2 flex items-center gap-2"><Checkbox data-testid="grievance-form-anon" checked={form.is_anonymous} onCheckedChange={(v) => set("is_anonymous")(!!v)} id="g-anon" /><Label htmlFor="g-anon" className="text-sm text-[hsl(var(--muted-ink))]">Submit anonymously (name & email optional; we won’t be able to update you individually)</Label></div>
      <div className="md:col-span-2 flex justify-end"><Button disabled={loading} type="submit" data-testid="grievance-form-submit" className="btn-primary">{loading ? "Submitting..." : "Submit grievance"}</Button></div>
    </form>
  );
};

const Field = ({ label, children }) => (
  <div><Label className="text-sm font-medium text-foreground">{label}</Label><div className="mt-1.5">{children}</div></div>
);
